import { useEffect, useState } from "react";
import chevRght from "../../assets/images/chev-right.svg";
import Checkbox from "../../components/layouts/CheckBox";
import TopHeader from "../../components/layouts/TopHeader";
import Button from "../../components/ui/Button";
import { ApiConstants } from "../../constants/ApiConstants";
import { ApiTypes } from "../../constants/Constants";
import useMakeRequest from "../../utils/ApiHelper";
import toast from "react-hot-toast";
import PageLayout from "../../components/ui/PageLayout";

type SectionKey = "chores" | "expenses" | "payment";

interface SectionData {
  expanded: boolean;
  options: Record<string, boolean>;
}

type SectionsState = Record<SectionKey, SectionData>;

// Mapping between UI labels and API field names
const apiFieldMapping: Record<string, string> = {
  "Chores newly assigned to me": "choresNewlyAssigned",
  "Overdue chores assigned to me": "overdueChores",
  "Daily list of chores assigned to me": "dailyList",
  "All completed chores": "allCompletedChores",
  "All unassigned chores": "unassignedChores",
  "Expenses shared with me": "expensesSharedWithMe",
  "All paid expenses": "paidExpenses",
  "All unpaid expenses": "unpaidExpenses",
  "All expenses": "allExpenses",
  "Reminders that contributions will be finalized soon": "finalizationNotice",
  "Finalized contributions": "finalizedContributions",
  "My balances": "myBalances",
  "Reminders of my balances owed": "remindersOwed",
  "Reminders of balances owed to me": "remindersOwedToMe",
  "All payments made to me": "paymentsMadeToMe",
  "All payments": "allPayments",
};

const initialState: SectionsState = {
  chores: {
    expanded: false,
    options: {
      "Chores newly assigned to me": false,
      "Overdue chores assigned to me": false,
      "Daily list of chores assigned to me": false,
      "All completed chores": false,
      "All unassigned chores": false,
    },
  },
  expenses: {
    expanded: false,
    options: {
      "Expenses shared with me": false,
      "All paid expenses": false,
      "All unpaid expenses": false,
      "All expenses": false,
    },
  },
  payment: {
    expanded: false,
    options: {
      "Reminders that contributions will be finalized soon": false,
      "Finalized contributions": false,
      "My balances": false,
      "Reminders of my balances owed": false,
      "Reminders of balances owed to me": false,
      "All payments made to me": false,
      "All payments": false,
    },
  },
};

const NotificationSettings = () => {
  const [sections, setSections] = useState<SectionsState>(initialState);
  const [emailNotifications, setEmailNotifications] = useState(false);
  const notificationList = useMakeRequest();
  const notificationPreference = useMakeRequest();

  console.log("emailNotifications: ", emailNotifications)

  useEffect(() => {
    GetNotificationSettingList();
  }, []);

  const GetNotificationSettingList = () => {
    notificationList
      .makeRequest({
        url: ApiConstants.GetNotificationSetting,
        type: ApiTypes.GET,
      })
      .then((res) => {
        console.log("res: ", res);
        if (res.status && res.data) {
          updateStateFromAPI(res.data);
        }
      })
      .catch((err) => {
        console.log("err: ", err.message);
      });
  };

  const updateStateFromAPI = (data: any) => {
    console.log("data: ", data)
    setEmailNotifications(data.email_notifications || false);

    const updatedSections = { ...sections };

    Object.keys(updatedSections).forEach((sectionKey) => {
      const section = updatedSections[sectionKey as SectionKey];
      const updatedOptions: Record<string, boolean> = {};

      Object.keys(section.options).forEach((optionLabel) => {
        const apiField = apiFieldMapping[optionLabel];
        if (apiField && data.hasOwnProperty(apiField)) {
          updatedOptions[optionLabel] = data[apiField];
        } else {
          updatedOptions[optionLabel] = section.options[optionLabel];
        }
      });

      updatedSections[sectionKey as SectionKey].options = updatedOptions;
    });

    setSections(updatedSections);
  };

  const preparePayloadForAPI = () => {
    const payload: any = {
      email_notifications: emailNotifications,
    };

    Object.values(sections).forEach((section) => {
      Object.entries(section.options).forEach(([label, value]) => {
        const apiField = apiFieldMapping[label];
        if (apiField) {
          payload[apiField] = value;
        }
      });
    });

    return payload;
  };

  const onUpdatePreference = () => {
    const payload = preparePayloadForAPI();
    console.log("preparePayloadForAPI: ", payload)
    notificationPreference
      .makeRequest({
        url: ApiConstants.UpdateNotificationSetting,
        type: ApiTypes.POST,
        payload: payload,
      })
      .then((res) => {
        if (res.status) {
          toast.success(res?.message || "Notification preferences saved successfully.");
          setTimeout(() => {
            GetNotificationSettingList();
          }, 1500);
        }
      })
      .catch((err) => {
        console.log("Error updating preferences:", err.message);
      });
  };

  const toggleExpand = (section: SectionKey) => {
    setSections((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        expanded: !prev[section].expanded,
      },
    }));
  };

  const toggleOption = (section: SectionKey, option: string) => {
    setSections((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        options: {
          ...prev[section].options,
          [option]: !prev[section].options[option],
        },
      },
    }));
  };

  const toggleAll = (section: SectionKey) => {
    const allChecked = Object.values(sections[section].options).every(Boolean);
    const updatedOptions = Object.fromEntries(
      Object.entries(sections[section].options).map(([key]) => [
        key,
        !allChecked,
      ])
    );

    setSections((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        options: updatedOptions,
      },
    }));
  };

  const handleEmailNotificationToggle = () => {
    setEmailNotifications(prev => !prev);
  };

  return (
    <>
      <PageLayout headerName='Notifications Settings' onlyTitle={false}>
        {/* <TopHeader headerName="Notifications Settings" /> */}
        <div className="notification-setting-page">
          <div className="notification-setting">

            {/* <div className="form-check form-switch ms-auto">
            <input className="form-check-input" type="checkbox" onClick={handleEmailNotificationToggle} checked={emailNotifications} />
          </div> */}
            <div className="page-padding-x">
              <Checkbox
                variant="toggle"
                id="emailNotifications"
                name="emailNotifications"
                title="Email Notifications"
                labelText="Notifications selected below will be sent through the Apple and Android apps. Would you also like to receive email notifications?"
                checked={emailNotifications}
                onChange={handleEmailNotificationToggle}
              />
            </div>
            <div className="divider" />
            <div className="page-padding-x">
              <div className="notification-setting-heading">
                Select Notifications
              </div>

              <div className="settings-panel">
                {Object.entries(sections).map(([key, section]) => {
                  const sectionKey = key as SectionKey;
                  const allSelected = Object.values(section.options).every(
                    Boolean
                  );
                  const parentCheckboxId = `checkbox-${sectionKey}-all`;

                  return (
                    <div className="section" key={sectionKey}>
                      <div
                        className="section-header"
                        onClick={() => toggleExpand(sectionKey)}
                      >
                        <div className="checkbox-wrapper" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            id={parentCheckboxId}
                            name={parentCheckboxId}
                            checked={allSelected}
                            onChange={() => toggleAll(sectionKey)}
                            labelText=""
                            customLabelClassName="category-check-box-label"
                          />
                        </div>
                        <label
                          className="section-label"
                        >
                          {sectionKey === "payment"
                            ? "Contributions & Payments"
                            : sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1)}
                        </label>
                        <span className={`arrow ${section.expanded ? 'expanded' : ''}`}>
                          <img
                            src={chevRght}
                            style={{ height: "24px", width: "24px" }}
                            alt="toggle"
                          />
                        </span>
                      </div>
                      {section.expanded && (
                        <div className="section-options">
                          {Object.entries(section.options).map(
                            ([label, checked], idx) => {
                              const checkboxId = `checkbox-${sectionKey}-${idx}`;
                              return (
                                <div
                                  className="option-item"
                                  key={label}
                                >
                                  <Checkbox
                                    id={checkboxId}
                                    name={checkboxId}
                                    labelText={label}
                                    checked={checked}
                                    onChange={() =>
                                      toggleOption(sectionKey, label)
                                    }
                                  />
                                </div>
                              );
                            }
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="page-padding-x">
            <Button
              text='Update'
              className='com-btn my-5 w-100'
              onClick={onUpdatePreference}
              disabled={notificationPreference.loading}
              loading={notificationPreference.loading}>
              Update
            </Button>
          </div>
        </div>
      </PageLayout>
    </>
  );
};

export default NotificationSettings;